import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ORDER_TYPES = ['Bulk Order (Retailer)', 'Custom Design', 'Wholesale Enquiry', 'Collaboration / Tie-up', 'Other'];
const FABRICS = ['Pure Cotton', 'Kota Doria', 'Mal Mal Doria', 'Chanderi', 'Silk', 'Chiffon', 'Viscose', 'Mix / Not Sure'];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const initialForm = { name: '', phone: '', city: '', business_name: '', order_type: '', preferred_fabric: '', quantity: '', message: '' };

export default function BulkOrderSection() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.phone.trim() || form.phone.length < 7) e.phone = 'Valid phone number required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.business_name.trim()) e.business_name = 'Store / Business name required';
    if (!form.order_type) e.order_type = 'Please select enquiry type';
    if (!form.message.trim() || form.message.length < 10) e.message = 'Please share some design requirements (min 10 chars)';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus('loading');

    const payload = {
      name: form.name,
      phone: form.phone,
      city: form.city,
      business_name: form.business_name,
      order_type: form.order_type,
      preferred_fabric: form.preferred_fabric || 'Not specified',
      quantity: form.quantity || 'Not specified',
      message: form.message,
    };
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    // 1) Always save to backend (so leads are never lost even if EmailJS fails)
    let backendOk = false;
    try {
      const r = await fetch(`${API_URL}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, source: 'bulk_order_form' }),
      });
      backendOk = r.ok;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Backend enquiry save failed:', err);
    }

    // 2) Send email via EmailJS (best-effort)
    let emailOk = false;
    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, payload, { publicKey: PUBLIC_KEY });
      emailOk = true;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('EmailJS error:', err);
    }

    if (backendOk || emailOk) {
      setStatus('success');
      setForm(initialForm);
      if (window.posthog) window.posthog.capture('bulk_order_enquiry_sent', { city: payload.city, order_type: payload.order_type, email_ok: emailOk, backend_ok: backendOk });
    } else {
      setStatus('error');
    }
  };

  return (
    <section id="enquiry" data-testid="bulk-order-section" className="py-24 md:py-32 bg-[#F5F0E8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 md:gap-20 items-start">

          {/* Left — Info */}
          <motion.div variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.span variants={fadeUp} className="inline-block text-xs font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium mb-4">
              Retailers & Wholesale
            </motion.span>
            <motion.h2 variants={fadeUp} className="font-serif font-light text-[#2D4A2D] text-4xl md:text-5xl leading-[1.1] tracking-tight mb-6">
              Bulk Orders &<br /><span className="italic">Custom Enquiries</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans font-light text-[#5C6B5C] text-base leading-relaxed mb-8">
              Boutique owner? Retailer? Fashion curator? Hum aapke saath kaam karna chahte hain.
              Share your requirements and we'll get back within 24 hours.
            </motion.p>

            <motion.div variants={fadeUp} className="space-y-5">
              {[
                ['Minimum Order', 'Flexible MOQ for first-time partners'],
                ['Fabrics Available', 'Cotton, Kota Doria, Chanderi, Silk, Chiffon, Viscose & more'],
                ['Customisation', 'Custom stitches, coloured thread, fabric choices on request'],
                ['Turnaround', '15–30 days depending on quantity & complexity'],
              ].map(([title, desc]) => (
                <div key={title} className="flex gap-4 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-2.5 flex-shrink-0" />
                  <div>
                    <div className="font-sans font-medium text-[#2D4A2D] text-sm mb-0.5">{title}</div>
                    <div className="font-sans font-light text-[#5C6B5C] text-sm leading-snug">{desc}</div>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp} className="mt-10 p-5 border border-[#D5CCBF] bg-[#E8DED1]">
              <div className="text-xs font-sans uppercase tracking-[0.2em] text-[#C9A84C] font-medium mb-1">Direct Contact</div>
              <a href="mailto:hello.weavesoflko@gmail.com" className="font-sans text-[#2D4A2D] text-sm hover:text-[#C9A84C] transition-colors">
                hello.weavesoflko@gmail.com
              </a>
            </motion.div>
          </motion.div>

          {/* Right — Form */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.2 }}>
            {status === 'success' ? (
              <div data-testid="form-success" className="bg-[#E8DED1] border border-[#D5CCBF] p-10 text-center">
                <CheckCircle size={40} strokeWidth={1} className="text-[#2D4A2D] mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-[#2D4A2D] mb-3">Enquiry Received!</h3>
                <p className="font-sans font-light text-[#5C6B5C] text-sm leading-relaxed mb-6">
                  Thank you for reaching out. We'll get back to you at <strong>hello.weavesoflko@gmail.com</strong> within 24 hours.
                </p>
                <button onClick={() => setStatus('idle')} className="font-sans text-sm text-[#2D4A2D] underline underline-offset-4 hover:text-[#C9A84C] transition-colors">
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="bulk-order-form" className="space-y-5">
                {/* Row: Name + Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Your Name *" error={errors.name}>
                    <input name="name" value={form.name} onChange={handleChange} placeholder="Priya Sharma" data-testid="field-name" className={inputCls(errors.name)} />
                  </Field>
                  <Field label="Phone *" error={errors.phone}>
                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" data-testid="field-phone" className={inputCls(errors.phone)} />
                  </Field>
                </div>

                {/* Row: City + Business */}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="City *" error={errors.city}>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="Mumbai" data-testid="field-city" className={inputCls(errors.city)} />
                  </Field>
                  <Field label="Store / Business Name *" error={errors.business_name}>
                    <input name="business_name" value={form.business_name} onChange={handleChange} placeholder="Your boutique" data-testid="field-business" className={inputCls(errors.business_name)} />
                  </Field>
                </div>

                {/* Enquiry Type */}
                <Field label="Enquiry Type *" error={errors.order_type}>
                  <select name="order_type" value={form.order_type} onChange={handleChange} data-testid="field-order-type" className={inputCls(errors.order_type)}>
                    <option value="">Select type...</option>
                    {ORDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>

                {/* Fabric + Quantity */}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Preferred Fabric">
                    <select name="preferred_fabric" value={form.preferred_fabric} onChange={handleChange} className={inputCls()}>
                      <option value="">Select fabric...</option>
                      {FABRICS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </Field>
                  <Field label="Approx. Quantity">
                    <input name="quantity" value={form.quantity} onChange={handleChange} placeholder="e.g. 50 pieces" className={inputCls()} />
                  </Field>
                </div>

                {/* Message */}
                <Field label="Design Requirements & Message *" error={errors.message}>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder="Describe your requirements, preferred designs, customisation needs, or any questions..." data-testid="field-message" className={`${inputCls(errors.message)} resize-none`} />
                </Field>

                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-600 text-sm font-sans">
                    <AlertCircle size={16} strokeWidth={1.5} />
                    Something went wrong. Please try again or email us directly.
                  </div>
                )}

                <button type="submit" disabled={status === 'loading'} data-testid="form-submit-btn"
                  className="w-full flex items-center justify-center gap-2.5 bg-[#2D4A2D] text-[#F5F0E8] py-4 font-sans font-medium text-sm tracking-wide transition-all duration-300 hover:bg-[#1A2A1A] hover:text-[#C9A84C] disabled:opacity-60 disabled:cursor-not-allowed">
                  {status === 'loading' ? <><Loader size={18} strokeWidth={1.5} className="animate-spin" /> Sending...</> : <><Send size={18} strokeWidth={1.5} /> Send Enquiry</>}
                </button>

                <p className="text-xs font-sans text-[#5C6B5C] text-center leading-snug">
                  We respond within 24 hours · No spam · Your details are private
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const inputCls = (err) =>
  `w-full bg-transparent border ${err ? 'border-red-400' : 'border-[#D5CCBF]'} px-4 py-3 font-sans font-light text-sm text-[#1A2A1A] placeholder-[#5C6B5C]/60 focus:outline-none focus:border-[#2D4A2D] transition-colors duration-200`;

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block font-sans text-xs uppercase tracking-[0.15em] text-[#2D4A2D] font-medium mb-2">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs font-sans text-red-500">{error}</p>}
    </div>
  );
}
